import { CommonModule } from '@angular/common';
import { Component, Inject, NgZone, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { AuthService, CommonService, RazorpayService, ToasterService } from '../../services';
import { CommonFunctions } from '../../classes';
import { Constants, GlobalSettings } from '../../common';
import { BytesToGbPipe } from '../bytes-to-gb-pipe';
import { Country, IBillingAddress, ICreateOrderResponse, IPaymentPlanDetails, IPlanDetails, IRazorpayOptions, IRazorpayPaymentResponse, IResponseMessage, ISubscription, ISubscriptionDialogData, ISubscriptionResponseAPI, IVerifyPaymentResponse, State } from '../../models';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";

declare class Razorpay {
  constructor(options: IRazorpayOptions);
  open(): void;
}

@Component({
  selector: 'app-plan-update',
  imports: [
    MatRadioModule,
    MatIconModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatFormFieldModule,
    BytesToGbPipe,
    MatInputModule,
    MatTooltipModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './plan-update.component.html',
  styleUrl: './plan-update.component.scss'
})
export class PlanUpdateComponent implements OnInit {

  dateFormat: string = Constants.dateFormat;

  selectedPlan: 'monthly' | 'yearly' = 'monthly';

  limits = GlobalSettings.characterLimits;

  currentPlanScreenCount: number = Constants.subscriptionConstants.defaultNumberOfScreens;
  numberOfScreens: number = Constants.subscriptionConstants.defaultNumberOfScreens;
  gstRate = Constants.subscriptionConstants.gstRate;
  yearlyDiscountPercentage: number = Constants.subscriptionConstants.yearlyDiscountPercentage;
  minScreens = Constants.subscriptionConstants.minScreens;
  maxScreens = Constants.subscriptionConstants.maxScreens;

  editBillingAddressForm!: FormGroup;
  billingAddress: IBillingAddress = {} as IBillingAddress;

  isSubscriptionUpdated: boolean = false;
  isVerifyPaymentLoading: boolean = false;
  isPaymentSuccess: boolean = false;
  reloadCounter: number = 3;
  subscriptionUpdatedMessage: string = 'Subscription Updated!'

  countries: Country[] = [];
  states: State[] = [];

  selectedTab = 'tab1';
  billingAction: 'purchase' | 'renewal' | 'upgrade' | 'reallocation' = 'purchase';
  proratedPlanAmount = 0;

  planSummary = {
    screens: 0,
    perScreenRate: 0,
    storage: 0,
    users: 0,
    totalAmount: 0,
    totalAmountAdjusted: 0,
    totalAmountPayable: 0,
    totalPerMonth: 0,
    nextPaymentDate: new Date(),
    validityDays: 0,
    gstAmount: 0,
    unconsumedAmount: 0
  };

  constructor(private dialogRef: MatDialogRef<PlanUpdateComponent>, @Inject(MAT_DIALOG_DATA) public data: ISubscriptionDialogData, private commonService: CommonService, private router: Router,
    private authService: AuthService, private razorpayService: RazorpayService, private toaster: ToasterService, private fb: FormBuilder, private ngZone: NgZone) {
    this.initializeForms();
  }

  ngOnInit(): void {
    // If value is passed, use it; otherwise, use default
    this.currentPlanScreenCount = this.data?.screenCount ?? Constants.subscriptionConstants.defaultNumberOfScreens;
    // Set the minimum allowed screens to this value
    // this.minScreens = this.currentPlanScreenCount;
    // Sync the main screen count variable
    this.numberOfScreens = this.currentPlanScreenCount;
    const subscriptionType = this.commonService.subscriptionDetail?.subscription_type;
    if (subscriptionType === 'monthly' || subscriptionType === 'yearly') this.selectedPlan = subscriptionType;
    this.updatePlanSummary();
    this.initializeForms();
    this.loadBillingDetails();
  }

  formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  loadBillingDetails() {
    this.commonService.getBillingInfo().subscribe({
      next: (billingAddress: IBillingAddress[]) => {
        this.billingAddress = billingAddress?.length > 0 ? billingAddress[0] : {} as IBillingAddress;
        this.initializeForms();
        this.initializeCountryState();
        // console.log(billingAddress);
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
      }
    })
  }


  initializeForms() {
    const billingAddress: IBillingAddress = this.billingAddress ? this.billingAddress : {} as IBillingAddress;
    billingAddress.country = isNaN(+billingAddress.country) ? billingAddress.country : +billingAddress.country;
    billingAddress.state = isNaN(+billingAddress.state) ? billingAddress.state : +billingAddress.state;
    this.editBillingAddressForm = this.fb.group({
      id: [billingAddress.id],
      full_name: [billingAddress.full_name, [Validators.required]],
      address_1: [billingAddress.address_1, [Validators.required]],
      address_2: [billingAddress.address_2, [Validators.required]],
      country: [billingAddress.country, [Validators.required]],
      state: [billingAddress.state, [Validators.required]],
      city: [billingAddress.city, [Validators.required]],
      zip_code: [billingAddress.zip_code, [Validators.required, Validators.pattern(Constants.zipCodePattern)]],
      mobile_number: [billingAddress.mobile_number, [Validators.pattern(Constants.mobilePattern)]],
    });
  }

  initializeCountryState() {
    this.commonService.getCountries().subscribe((res) => {
      this.countries = res;
      if (this.editBillingAddressForm.value.country) {
        this.getStatesData(this.editBillingAddressForm.value.country)
      }
    });

    // When country changes, load states
    this.editBillingAddressForm.get('country')?.valueChanges.subscribe((countryId) => {
      this.editBillingAddressForm.get('state')?.setValue('');
      this.getStatesData(countryId);
    });
  }

  getStatesData(countryId: string) {
    if (countryId) {
      this.commonService.getStates(countryId).subscribe((res) => {
        this.states = res;
      });
    } else {
      this.states = [];
    }
  }

  getFormHeight() {
    return window.innerHeight - 320;
  }

  getNextPaymentDate(): Date {
    return this.planSummary.nextPaymentDate;
  }

  private hasActivePaidPlan(): boolean {
    const subscription = this.commonService.subscriptionDetail;
    return !!subscription?.is_active && subscription.status === 'live'
      && new Date(subscription.expiry_date).getTime() > Date.now();
  }

  private isActiveScreenReduction(): boolean {
    return this.hasActivePaidPlan() && this.numberOfScreens > 0
      && this.numberOfScreens < Number(this.commonService.subscriptionDetail.screen_limit);
  }

  getUnconsumedAmount(): number {
    const subscription: ISubscription = this.commonService.subscriptionDetail;
    const balance = Number(subscription?.balance_amount);
    if (Number.isFinite(balance) && balance > 0) return balance;
    if (!this.hasActivePaidPlan()) return 0;

    // Older subscription responses omit the unused balance. Preserve the paid
    // capacity remaining until expiry rather than charging for a fresh term.
    const remainingDays = (new Date(subscription.expiry_date).getTime() - Date.now()) / 86400000;
    const screens = Number(subscription.screen_limit);
    const rate = Number(subscription.per_screen_amount) || this.getPlanSlotDetails(screens).rate;
    const yearly = subscription.subscription_type === 'yearly';
    const termCost = screens * rate * (yearly ? 12 * (1 - this.yearlyDiscountPercentage) : 1);
    return Math.max(0, termCost / (yearly ? 365 : 30) * remainingDays);
  }

  getYearlyTotalAmount(): number {
    // const slotDetails = this.getPlanSlotDetails(this.numberOfScreens);
    // const yearlyTotal = slotDetails.rate * this.numberOfScreens * 12;
    // return yearlyTotal - (yearlyTotal * this.yearlyDiscountPercentage);
    const slotDetails = this.getPlanSlotDetails(this.numberOfScreens);
    const monthlyTotal = slotDetails.rate * this.numberOfScreens;
    return monthlyTotal - (monthlyTotal * this.yearlyDiscountPercentage);
  }

  getMonthlyTotalAmount(): number {
    const slotDetails = this.getPlanSlotDetails(this.numberOfScreens);
    const monthlyTotal = slotDetails.rate * this.numberOfScreens;
    return monthlyTotal;
  }

  // Function to calculate the save amount (discount) for the yearly plan
  getSaveAmount(): number {
    const slotDetails = this.getPlanSlotDetails(this.numberOfScreens);
    const yearlyTotal = slotDetails.rate * this.numberOfScreens * 12;
    // return this.selectedPlan === 'monthly' ? 0 : (yearlyTotal * this.yearlyDiscountPercentage);
    return (yearlyTotal * this.yearlyDiscountPercentage);
  }

  getPlanSlotDetails(screens: number): { rate: number, storage: number, users: number } {
    if (screens < 5) {
      return { rate: 800, storage: 5, users: 3 };
    } else if (screens < 10) {
      return { rate: 760, storage: 10, users: 5 };
    } else if (screens < 20) {
      return { rate: 720, storage: 15, users: 8 };
    } else if (screens < 50) {
      return { rate: 700, storage: 25, users: 10 };
    } else if (screens < 100) {
      return { rate: 650, storage: 50, users: 20 };
    } else if (screens < 200) {
      return { rate: 600, storage: 100, users: 30 };
    } else if (screens < 300) {
      return { rate: 520, storage: 150, users: 40 };
    } else {
      // Extend this logic as needed
      return { rate: 520, storage: 150, users: 40 }; // cap it
    }
  }

  updatePlanSummary() {
    const screenCount = this.numberOfScreens || 0;
    const slotDetails = this.getPlanSlotDetails(screenCount);

    const monthlyTotal = screenCount * slotDetails.rate;
    const yearlyTotal = monthlyTotal * 12;

    const isYearly = this.selectedPlan === 'yearly';
    const total = isYearly ? yearlyTotal : monthlyTotal;
    // * (1 - this.yearlyDiscountPercentage)

    const termDays = isYearly ? 365 : 30;
    const totalAmount = total;
    const discountAmount = (this.selectedPlan === 'yearly') ? (totalAmount * this.yearlyDiscountPercentage) : 0;
    const netTermCost = totalAmount - discountAmount;
    const subscription = this.commonService.subscriptionDetail;
    const active = this.hasActivePaidPlan();
    const sameCycle = subscription?.subscription_type === this.selectedPlan;
    const sameCapacity = screenCount === Number(subscription?.screen_limit);
    const now = new Date();
    const expiry = new Date(subscription?.expiry_date);
    const remainingDays = active ? (expiry.getTime() - now.getTime()) / 86400000 : 0;
    let unconsumedAmount = 0;
    let grandTotal = netTermCost;
    let nextPaymentDate = new Date(now);
    nextPaymentDate.setDate(nextPaymentDate.getDate() + termDays);
    this.billingAction = 'purchase';
    this.proratedPlanAmount = 0;

    if (active && sameCycle && sameCapacity) {
      // Renewal buys a complete additional term. Existing days remain intact.
      this.billingAction = 'renewal';
      nextPaymentDate = new Date(expiry);
      nextPaymentDate.setDate(nextPaymentDate.getDate() + termDays);
    } else if (active && sameCycle && screenCount > Number(subscription.screen_limit)) {
      // Upgrade only the remaining paid period, using complete tier prices.
      this.billingAction = 'upgrade';
      unconsumedAmount = this.getUnconsumedAmount();
      const remainingNewCost = netTermCost / termDays * remainingDays;
      this.proratedPlanAmount = Math.round(remainingNewCost * 100) / 100;
      grandTotal = Math.max(0, remainingNewCost - unconsumedAmount);
      nextPaymentDate = new Date(expiry);
      // Crossing a pricing tier can lower the overall daily cost.
      if (remainingNewCost < unconsumedAmount && netTermCost > 0) {
        this.billingAction = 'reallocation';
        nextPaymentDate = new Date(now);
        nextPaymentDate.setDate(nextPaymentDate.getDate() + Math.floor(unconsumedAmount / (netTermCost / termDays)));
      }
    } else if (active) {
      // Reductions and billing-cycle changes preserve the existing paid value.
      unconsumedAmount = this.getUnconsumedAmount();
      grandTotal = this.isActiveScreenReduction() ? 0 : Math.max(0, netTermCost - unconsumedAmount);
      if (grandTotal === 0 && netTermCost > 0 && unconsumedAmount > 0) {
        this.billingAction = 'reallocation';
        nextPaymentDate = new Date(now);
        nextPaymentDate.setDate(now.getDate() + Math.floor(unconsumedAmount / (netTermCost / termDays)));
      }
    }
    grandTotal = Math.round(grandTotal * 100) / 100;
    const gstAmount = Math.round(grandTotal * this.gstRate * 100) / 100;
    const totalAmountPayable = Math.round((grandTotal + gstAmount) * 100) / 100;
    const validity = Math.max(0, Math.ceil((nextPaymentDate.getTime() - now.getTime()) / 86400000));

    this.planSummary = {
      screens: screenCount,
      perScreenRate: slotDetails.rate,
      users: slotDetails.users,
      storage: this.gbToBytes(slotDetails.storage),
      totalPerMonth: total,
      nextPaymentDate,
      validityDays: validity,
      gstAmount,
      totalAmount,
      totalAmountAdjusted: grandTotal,
      totalAmountPayable,
      unconsumedAmount
    };
  }

  gbToBytes(gb: number): number {
    return gb * 1024 * 1024 * 1024;
  }

  sanitizeScreenInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove leading zeros except when value is exactly "0"
    value = value.replace(/^0+(?=\d)/, '');

    // Convert to number or 0 if empty
    let num = value === '' ? 0 : +value;

    // Clamp the value between minScreens and maxScreens
    // const min = this.minScreens ?? 0; // fallback if undefined
    const min = 0;
    const max = this.maxScreens ?? 99; // fallback if undefined

    if (num < min) num = min;
    if (num > max) num = max;

    // Update the bound ngModel and the input field visually
    this.numberOfScreens = num;
    input.value = num.toString();

    // You can call your previous logic if needed:
    this.onPlanOrScreenChange?.();
  }


  onPlanOrScreenChange() {
    this.updatePlanSummary();
  }

  backButtonClick() {
    this.closeDialog();
  }

  closeDialog(): void {
    if (this.isSubscriptionUpdated) {
      this.commonService.refreshSubscription.next();
      this.router.navigate(['']);
      window.location.reload();
    }
    else {
      if (this.isSubscriptionExpired()) {
        this.dialogRef.close();
        this.commonService.refreshSubscription.next();
      }
      else {
        this.dialogRef.close();
      }
    }
  }

  isSubscriptionExpired() {
    if (CommonFunctions.getTrialsRemainingMinutes(this.commonService.subscriptionDetail) <= 0) {
      return true;
    }
    return false;
  }

  logout() {
    this.closeDialog();
    this.authService.logout();
  }

  onFormSubmit() {
    if (this.planSummary.screens < this.minScreens || this.planSummary.screens > this.maxScreens) {
      this.toaster.warning(`Please select a number of screens between ${this.minScreens} and ${this.maxScreens}`);
    }
    else {
      this.submitBillingandPlan();
    }
  }

  submitBillingandPlan() {
    if (this.editBillingAddressForm.valid) {
      if (this.billingAddress.id) {
        this.commonService.isLoading = true;
        this.commonService.updateBillingInfo(this.billingAddress.id, this.editBillingAddressForm.value).subscribe({
          next: (response: IResponseMessage) => {
            // console.log(response);
            this.updatePlan();
            this.loadBillingDetails();
          },
          error: (error: HttpErrorResponse) => {
            this.commonService.isLoading = false;
            console.log(error);
            this.toaster.error(error?.error?.message ? error.error.message : Constants.notifyMessages.updateErrorBillingInfo);
          }
        })
      }
      else {
        this.commonService.isLoading = true;
        this.commonService.addBillingInfo(this.editBillingAddressForm.value).subscribe({
          next: (response: IResponseMessage) => {
            // console.log(response);
            this.updatePlan();
            this.loadBillingDetails();
          },
          error: (error: HttpErrorResponse) => {
            this.commonService.isLoading = false;
            console.log(error);
            this.toaster.error(error?.error?.message ? error.error.message : Constants.notifyMessages.addErrorBillingInfo);
          }
        })
      }
    }
    else {
      if (!this.editBillingAddressForm.valid) {
        this.toaster.warning(Constants.notifyMessages.invalidForm);
      }
      Object.values(this.editBillingAddressForm.controls).forEach(control => {
        control.markAsTouched();
      });
    }
  }

  updatePlan() {
    if (this.planSummary.screens < this.minScreens || this.planSummary.screens > this.maxScreens) {
      this.toaster.warning(`Please select a number of screens between ${this.minScreens} and ${this.maxScreens}`);
      return;
    }
    else {
      this.initiatePayment();
    }
  }

  initiatePayment() {
    const requiresPayment = this.planSummary.totalAmountPayable > 0;
    const planDetails: IPlanDetails = {
      screen_limit: this.planSummary.screens,
      storage_limit: this.planSummary.storage,
      members_limit: this.planSummary.users,
      validity_period: this.planSummary.validityDays,
      amount: this.planSummary.totalAmountPayable,
      discount_percentage: (this.selectedPlan === 'yearly') ? this.yearlyDiscountPercentage : 0,
      discount_amount: (this.selectedPlan === 'yearly') ? this.getSaveAmount() : 0,
      gst_percentage: this.gstRate,
      gst_amount: this.planSummary.gstAmount,
      perscreen_amount: this.planSummary.perScreenRate,
      subscription_type: this.selectedPlan,
      expiry_date: this.planSummary.nextPaymentDate.toISOString()
    };

    this.commonService.isLoading = true;
    this.razorpayService.createOrder(planDetails).subscribe({
      next: (response: ICreateOrderResponse) => {
        this.commonService.isLoading = false;
        // console.log(response);
          if (!requiresPayment) {
            // The order endpoint applies balance-only subscription changes.
            // Never launch checkout for a change quoted at zero payable.
            if (response?.message && !(Number(response.amount) > 0)) {
              this.subscriptionUpdatedMessage = response.message;
              this.isSubscriptionUpdated = true;
              this.commonService.refreshSubscription.next();
            } else {
              this.toaster.error('Could not confirm the subscription update. Please refresh your subscription details before retrying.');
            }
          }
          else if (response && response.order_id && response.amount > 0) {
          this.payNow(response);
        }
        else if (response.message) {
          this.subscriptionUpdatedMessage = response.message;
          this.isSubscriptionUpdated = true;
        }
        else {
          console.error('Invalid response from Razorpay API:', response);
        }
      }, error: (error: HttpErrorResponse) => {
        this.commonService.isLoading = false;
        console.error('Error creating Razorpay order:', error);
        this.toaster.error(error?.error?.message ? error.error.message : 'Error creating Razorpay order');
      }
    });
  }

  payNow(response: ICreateOrderResponse) {
    const options: IRazorpayOptions = {
      description: 'ZignFlix Subscription',
      currency: response.currency,
      amount: response.amount,
      name: this.commonService.profileDetail.full_name,
      key: response.razorpay_key_id,
      image: 'https://zignflix.com/assets/images/favicon.svg',
      order_id: response.order_id, // Required for signature verification
      prefill: {
        name: this.commonService.profileDetail.full_name,
        email: this.commonService.profileDetail.email
      },
      theme: {
        color: '#FCEEF7'
      },
      handler: (paymentResponse: IRazorpayPaymentResponse) => {

        this.ngZone.run(() => {
          // 👇 Send payment data to backend for verification
          const paymentPlanDetails: IPaymentPlanDetails = {
            razorpay_order_id: paymentResponse.razorpay_order_id,
            razorpay_payment_id: paymentResponse.razorpay_payment_id,
            razorpay_signature: paymentResponse.razorpay_signature,
            screen_limit: this.planSummary.screens,
            storage_limit: this.planSummary.storage,
            members_limit: this.planSummary.users,
            validity_period: this.planSummary.validityDays,
            amount: this.planSummary.totalAmount,
            status: 'live',
            expiry_date: this.planSummary.nextPaymentDate,
            discount_percentage: (this.selectedPlan === 'yearly') ? this.yearlyDiscountPercentage : 0,
            discount_amount: (this.selectedPlan === 'yearly') ? this.getSaveAmount() : 0,
            gst_percentage: this.gstRate,
            gst_amount: this.planSummary.gstAmount,
            perscreen_amount: this.planSummary.perScreenRate,
            subscription_type: this.selectedPlan
          };
          this.isVerifyPaymentLoading = true;
          this.razorpayService.verifyPayment(paymentPlanDetails).subscribe({
            next: (res: IVerifyPaymentResponse) => {
              this.isVerifyPaymentLoading = false;
              this.isPaymentSuccess = true;

              // Optional toaster
              this.toaster.success(res?.message || 'Subscription activated successfully');

              // Start countdown
              this.startReloadCountdown();
            },
            error: (err: HttpErrorResponse) => {
              this.toaster.error(err.message || 'Payment verification failed');
              this.isVerifyPaymentLoading = false;
            }
          });
        }
        );
      },
      modal: {
        ondismiss: () => {
          // console.log('Payment popup dismissed');
        }
      }
    };

    const rzp = new Razorpay(options);
    rzp.open();
  }

  startReloadCountdown() {
    const interval = setInterval(() => {
      this.reloadCounter--;

      if (this.reloadCounter === 0) {
        clearInterval(interval);

        this.commonService.getSubscriptionDetails().subscribe({
          next: (data: ISubscriptionResponseAPI) => {
            const subscription = data.data;
            this.commonService.subscriptionDetail = subscription;
            this.commonService.refreshSubscription.next();

            this.router.navigate(['']);
            window.location.reload();
          },
          error: () => {
            window.location.reload();
          }
        });
      }
    }, 1000);
  }
}
